import { useContext, useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { FirebaseError } from '@firebase/util';
import { db } from '../api/firebase/config';
import { AuthContext } from '../contexts/auth';
import { useCompanyId } from './useCompanyId';
import { isCompanyAdmin, isOperatorStaff } from '../utils/permission';

/**
 * 右上アカウントメニュー / 各ページに「セキュリティ」導線を出すかどうかを判定する（#2982-2985）。
 *
 * - companyAdmin / 代理モード: `has_security_addon = true` の場合に true
 * - 運営スタッフ自身のセッション: `has_security_addon = true` であれば事前確認のため true
 * - companyMember 等その他のロールでは常に false（自身の MFA 設定はマイページ側で行う）
 *
 * API 機能（useApiMenuVisibility）と異なり利用開始日の概念は無く、フラグのみで判定する。
 */
export const useSecurityAddonMenuVisibility = (): boolean => {
  const { user } = useContext(AuthContext);
  const { companyId: effectiveCompanyId, isProxyMode } = useCompanyId();
  const [hasSecurityAddon, setHasSecurityAddon] = useState(false);

  useEffect(() => {
    const companyIdToUse = effectiveCompanyId ?? user?.company_id;
    if (!companyIdToUse) {
      setHasSecurityAddon(false);
      return;
    }
    const ref = doc(db, 'companies', companyIdToUse);
    const unsubscribe = onSnapshot(
      ref,
      snapshot => {
        if (!snapshot.exists()) {
          setHasSecurityAddon(false);
          return;
        }
        setHasSecurityAddon(snapshot.data().has_security_addon === true);
      },
      error => {
        // ログアウト直後の permission-denied は正常動作
        if (error instanceof FirebaseError && error.code === 'permission-denied') {
          return;
        }
      },
    );
    return (): void => unsubscribe();
  }, [effectiveCompanyId, user?.company_id]);

  if (!user || !hasSecurityAddon) return false;
  const isCustomerView = isCompanyAdmin(user.role) || isProxyMode;
  return isCustomerView || isOperatorStaff(user.role);
};
